"use client"

import type { AttackType } from "@/types/hero.types"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { DamageTypeTooltip } from "@/components/ui/damage-type-tooltip"

interface DamageBarProps {
  min: number
  max: number
  scale: number
  bonus?: number
  damageType?: AttackType
  variant?: "game" | "shop"
}

export const DamageBar = ({ min, max, scale, bonus = 0, damageType, variant = "game" }: DamageBarProps) => {
  // Shop variant has different positioning, width, and no bonus display
  const width = variant === "game" ? 64 * scale : 48 * scale
  const height = 17 * scale
  const left = variant === "game" ? 128 * scale : 122 * scale
  const top = 113 * scale
  const iconSize = Math.max(12, 14 * scale)

  const barId = variant === "game" ? "damage-bar" : "shop-damage-bar"

  // Shop variant has larger font and no cursor-help
  const fontSize = variant === "game" ? Math.max(5, 6 * scale) : Math.max(7, 9 * scale)
  const cursorClass = variant === "game" ? "cursor-help" : ""

  const content = (
    <div
      id={barId}
      className={`absolute flex items-center gap-1 ${cursorClass}`}
      style={{
        left,
        top,
        width,
        height,
      }}
    >
      <img
        src="/images/damage-hero.png"
        alt="Damage"
        width={iconSize}
        height={iconSize}
        style={{ imageRendering: "pixelated" }}
      />
      <span
        className="text-white font-bold drop-shadow-lg"
        style={{
          fontSize,
          textShadow: "0 1px 2px black",
        }}
      >
        {min}-{max}
        {variant === "game" && bonus > 0 && (
          <span className="text-green-400 text-[0.85em]"> +{bonus}</span>
        )}
      </span>
    </div>
  )

  // Shop variant never has tooltips, game variant only if damageType provided
  if (variant === "shop" || !damageType) {
    return content
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-black/95 border border-cyan-500/50 shadow-lg shadow-black/50 p-0"
        >
          <DamageTypeTooltip damageType={damageType} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
